import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import UserAvatar from "components/UserAvatar";
import { requireAuth } from "utilities/auth";
import { useWorkload } from "hooks/useWorkload";
import { SLA_TONE } from "utilities/sla";
import {
  Gauge,
  Inbox,
  AlertTriangle,
  Clock,
  UserX,
  Briefcase,
  Users as UsersIcon,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

/**
 * Workload (SOUND-538, Ticketing Phase 5.2) — the resource-management dashboard.
 * Reads GET /workload (SOUND-537): open ticket load + SLA breach-risk per
 * function/queue and per assignee, plus the unassigned bucket. Breach-risk uses
 * the SAME semantic tones as the SLA UI (utilities/sla SLA_TONE — warning/
 * destructive, never the brand accent). Internal-only.
 */
function Workload() {
  const { workload, loading, error, refetch } = useWorkload();

  const byFunction = useMemo(() => workload?.byFunction || [], [workload]);
  const byAssignee = useMemo(() => workload?.byAssignee || [], [workload]);
  const unassigned = useMemo(
    () => workload?.unassigned || { open: { total: 0 }, breach: { approaching: 0, breached: 0 } },
    [workload]
  );

  // KPI totals — every open ticket is either assigned or unassigned, so summing
  // byAssignee + unassigned is a clean partition (byFunction is a different cut).
  const totals = useMemo(() => {
    const sum = (rows, path) => rows.reduce((n, r) => n + (get(r, path) || 0), 0);
    const open =
      sum(byAssignee, "open.total") + (unassigned.open?.total || 0);
    const breached =
      sum(byAssignee, "breach.breached") + (unassigned.breach?.breached || 0);
    const approaching =
      sum(byAssignee, "breach.approaching") + (unassigned.breach?.approaching || 0);
    return { open, breached, approaching, unassigned: unassigned.open?.total || 0 };
  }, [byAssignee, unassigned]);

  // Busiest assignee's total drives the relative load bars.
  const maxAssigneeLoad = useMemo(
    () => byAssignee.reduce((m, a) => Math.max(m, a.open?.total || 0), 0),
    [byAssignee]
  );

  const isEmpty = !loading && !error && byFunction.length === 0 && byAssignee.length === 0;

  return (
    <div>
      <PageHeader
        title="Workload"
        subtitle="Open ticket load and SLA breach-risk across queues and the team. Breached is red, approaching amber — the same tones as the ticket SLA indicators."
        actions={
          <button className="btn btn-secondary" onClick={refetch} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          items={[
            { label: "Open tickets", value: totals.open, tone: "info", icon: <Inbox />, loading },
            { label: "Breached", value: totals.breached, tone: "destructive", icon: <AlertTriangle />, loading },
            { label: "Approaching", value: totals.approaching, tone: "warning", icon: <Clock />, loading },
            { label: "Unassigned", value: totals.unassigned, tone: "neutral", icon: <UserX />, loading },
          ]}
        />

        {error ? (
          <div className="surface p-8 text-center">
            <p className="text-destructive">Failed to load workload.</p>
            <button className="btn btn-ghost mt-3" onClick={refetch}>
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-40 w-full" />
            <Skeleton.Rect className="h-40 w-full" />
          </div>
        ) : isEmpty ? (
          <EmptyState
            icon={Gauge}
            heading="No workload yet"
            description="Open tickets across queues and the team will show here once they start flowing."
          />
        ) : (
          <>
            {/* Unassigned callout */}
            <UnassignedCallout unassigned={unassigned} />

            {/* By function / queue */}
            <section className="surface overflow-hidden rounded-xl ring-1 ring-border">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">By queue</h3>
                <span className="text-xs text-muted-foreground">· {byFunction.length}</span>
              </div>
              {byFunction.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">No queues configured.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <Th>Queue</Th>
                        <Th>Assignment</Th>
                        <Th>Owner</Th>
                        <Th>Members</Th>
                        <Th className="text-right">Open</Th>
                        <Th>Breach risk</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {byFunction.map((f) => (
                        <tr key={f.functionKey} className="transition-colors hover:bg-muted">
                          <td className={`px-4 py-3 ${riskBorder(f.breach)}`}>
                            <div className="font-medium text-foreground">{f.label || f.functionKey}</div>
                            <div className="font-mono text-xs text-muted-foreground">{f.functionKey}</div>
                          </td>
                          <td className="px-4 py-3">
                            <StrategyBadge strategy={f.strategy} />
                          </td>
                          <td className="px-4 py-3">
                            {f.ownerName ? (
                              <span className="inline-flex items-center gap-1.5">
                                <UserAvatar user={{ name: f.ownerName }} />
                                <span className="text-foreground">{f.ownerName}</span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">— None</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <MemberStack members={f.members} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <OpenCounts open={f.open} />
                          </td>
                          <td className="px-4 py-3">
                            <BreachRisk breach={f.breach} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* By assignee */}
            <section className="surface overflow-hidden rounded-xl ring-1 ring-border">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <UsersIcon className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">By assignee</h3>
                <span className="text-xs text-muted-foreground">· {byAssignee.length}</span>
              </div>
              {byAssignee.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nobody has open tickets.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <Th>Assignee</Th>
                        <Th className="w-52">Load</Th>
                        <Th className="text-right">Open</Th>
                        <Th>Breach risk</Th>
                        <Th className="text-right"> </Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {byAssignee.map((a) => (
                        <tr key={a.userId} className="transition-colors hover:bg-muted">
                          <td className={`px-4 py-3 ${riskBorder(a.breach)}`}>
                            <span className="inline-flex items-center gap-2">
                              <UserAvatar user={{ name: a.name }} />
                              <span className="font-medium text-foreground">{a.name || a.userId}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <LoadBar value={a.open?.total || 0} max={maxAssigneeLoad} atRisk={!!a.breach?.breached} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <OpenCounts open={a.open} />
                          </td>
                          <td className="px-4 py-3">
                            <BreachRisk breach={a.breach} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              to={`/tickets?assigneeId=${encodeURIComponent(a.userId)}`}
                              className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                              title="View this assignee's tickets"
                            >
                              View tickets
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* ── helpers ───────────────────────────────────────────────────────────────── */

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function riskBorder(breach) {
  if (breach?.breached) return "border-l-2 border-l-destructive";
  if (breach?.approaching) return "border-l-2 border-l-warning";
  return "";
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground ${className}`}>
      {children}
    </th>
  );
}

function OpenCounts({ open }) {
  const todo = open?.todo || 0;
  const inProgress = open?.in_progress || 0;
  const total = open?.total ?? todo + inProgress;
  return (
    <span className="inline-flex items-baseline gap-2" title={`${todo} to-do · ${inProgress} in progress`}>
      <span className="text-base font-semibold text-foreground">{total}</span>
      <span className="text-xs text-muted-foreground">
        {todo}·{inProgress}
      </span>
    </span>
  );
}

function BreachRisk({ breach }) {
  const breached = breach?.breached || 0;
  const approaching = breach?.approaching || 0;
  if (!breached && !approaching) {
    return <span className="text-xs text-muted-foreground">On track</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {breached > 0 && (
        <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium ${SLA_TONE.breached.chip} ${SLA_TONE.breached.chipBg}`}>
          <AlertTriangle className="h-3.5 w-3.5" />
          {breached} breached
        </span>
      )}
      {approaching > 0 && (
        <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium ${SLA_TONE.approaching.chip} ${SLA_TONE.approaching.chipBg}`}>
          <Clock className="h-3.5 w-3.5" />
          {approaching} approaching
        </span>
      )}
    </span>
  );
}

function StrategyBadge({ strategy }) {
  const capacity = strategy === "leastLoaded";
  return (
    <span
      className={`badge ${capacity ? "badge-info" : "badge-muted"}`}
      title={
        capacity
          ? "Capacity-based — new tickets route to the least-loaded holder (default owner is the fallback)."
          : "Fixed owner — new tickets auto-assign to the default owner."
      }
    >
      {capacity ? "Capacity-based" : "Fixed owner"}
    </span>
  );
}

function MemberStack({ members = [] }) {
  if (!members.length) return <span className="text-muted-foreground">—</span>;
  const shown = members.slice(0, 4);
  const extra = members.length - shown.length;
  return (
    <span className="inline-flex items-center">
      <span className="flex -space-x-2">
        {shown.map((m) => (
          <span key={m.userId} className="ring-2 ring-card rounded-full" title={m.name}>
            <UserAvatar user={{ name: m.name }} />
          </span>
        ))}
      </span>
      {extra > 0 && <span className="ml-2 text-xs text-muted-foreground">+{extra}</span>}
    </span>
  );
}

function LoadBar({ value, max, atRisk }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${atRisk ? "bg-destructive/70" : "bg-brand"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 shrink-0 text-right text-xs text-muted-foreground">{value}</span>
    </div>
  );
}

function UnassignedCallout({ unassigned }) {
  const total = unassigned.open?.total || 0;
  const breached = unassigned.breach?.breached || 0;
  const approaching = unassigned.breach?.approaching || 0;
  const risk = breached > 0 ? "breached" : approaching > 0 ? "approaching" : null;
  const border = risk === "breached" ? "border-destructive/30" : risk === "approaching" ? "border-warning/30" : "border-border";

  return (
    <div className={`surface flex flex-wrap items-center justify-between gap-4 rounded-xl border ${border} p-4`}>
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-md bg-muted text-muted-foreground">
          <UserX className="h-5 w-5" />
        </span>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-foreground">{total}</span>
            <span className="text-sm text-muted-foreground">unassigned open</span>
          </div>
          <p className="text-xs text-muted-foreground">
            No owner yet — pick these up or route them to a queue.
          </p>
        </div>
      </div>
      <BreachRisk breach={unassigned.breach} />
    </div>
  );
}

export default requireAuth(Workload);
